import { format, isValid, parseISO, parse } from "date-fns";
import logger from "./logger";
import { TFunction } from "i18next";

const formatDate = (date: any, formatPattern = "MM/dd/yyyy") => {
  let dateObj;

  if (!date) {
    return ""; // Retorna string vazia se a data for nula ou indefinida
  }

  if (typeof date === "string") {
    // Primeiro tenta converter como ISO
    dateObj = parseISO(date);

    // Se não for uma string ISO válida, tenta interpretar no formato recebido
    if (!isValid(dateObj)) {
      dateObj = parse(date, "M/d/yyyy, h:mm:ss a", new Date());
    }
  } else {
    dateObj = new Date(date); // Converte timestamps numéricos para Date
  }

  if (!isValid(dateObj)) {
    return ""; // Se a data for inválida, retorna string vazia
  }

  return format(dateObj, formatPattern);
};

const getRelativeTime = (
  timestamp: string | Date,
  t: TFunction<"translation", undefined>,
): string => {
  const now = new Date();
  const createdAt = new Date(timestamp);
  const diffMs = now.getTime() - createdAt.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return t("time.justNow");
  if (diffMin === 1) return t("time.minuteAgo");

  if (diffMin < 60) return t("time.minutesAgo", { count: diffMin });

  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs === 1) return t("time.hourAgo");
  if (diffHrs < 24) return t("time.hoursAgo", { count: diffHrs });

  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return t("time.dayAgo");
  return t("time.daysAgo", { count: diffDays });
};

/**
 * Formats a date according to the locale based on i18n language code
 * @param date - Date string, Date object, or timestamp
 * @param languageCode - i18n language code ("pt", "en", "es")
 * @returns Formatted date string according to locale conventions
 */
const formatLocaleDate = (
  date: string | Date | number,
  languageCode: string,
): string => {
  if (!date) {
    return "";
  }

  let dateObj: Date;

  try {
    if (typeof date === "string" || typeof date === "number") {
      dateObj = new Date(date);
    } else {
      dateObj = date;
    }

    if (isNaN(dateObj.getTime())) {
      return "";
    }
  } catch (error) {
    logger.error(`⚠️ Erro ao processar data: ${date}`, error);
    return "";
  }

  // Map i18n language codes to locale codes
  const localeMap: Record<string, string> = {
    pt: "pt-BR", // Brazilian Portuguese
    en: "en-US", // US English
    es: "es-ES", // Spanish
  };

  const locale = localeMap[languageCode] || "en-US";

  // Use Intl.DateTimeFormat to format according to locale conventions
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).format(dateObj);
};

export { formatDate, getRelativeTime, formatLocaleDate };
